/**
 * Componente de diálogo para compartir un álbum.
 * Adaptado para la web.
 */

import React, { useState } from 'react';
import Rodal from 'rodal';
import 'rodal/lib/rodal.css';
import { FiX } from 'react-icons/fi';

/**
 * Componente ShareAlbumDialog
 * 
 * Permite seleccionar usuario, tipo de acceso y fechas temporales para compartir un álbum.
 * 
 * @param {boolean} visible - Indica si el diálogo debe ser visible
 * @param {Function} onClose - Callback al cerrar el diálogo
 * @param {string} albumId - ID del álbum a compartir
 * @param {string} albumName - Nombre del álbum
 */
export default function ShareAlbumDialog({ 
  visible, 
  onClose, 
  albumId, 
  albumName 
}) {
  const [selectedFriendId, setSelectedFriendId] = useState('');
  const [accessType, setAccessType] = useState('unlimited');
  const [accessFrom, setAccessFrom] = useState('');
  const [accessUntil, setAccessUntil] = useState('');

  const handleGrantAccess = () => {
    if (!selectedFriendId.trim()) {
      alert('Por favor selecciona un usuario');
      return;
    }

    console.log('Otorgar acceso:', { albumId, albumName, selectedFriendId, accessType, accessFrom, accessUntil });
    
    // Implementar lógica de otorgar acceso cuando esté disponible
    setSelectedFriendId('');
    setAccessType('unlimited');
    setAccessFrom('');
    setAccessUntil('');
    onClose();
  };

  const getAccessTypeLabel = (type) => {
    switch (type) {
      case 'view_once': return 'Ver una sola vez';
      case '24_hours': return 'Ver por 24 horas';
      case 'unlimited': return 'Sin límite';
      default: return type;
    }
  };

  const getAccessTypeColor = (type) => {
    switch (type) {
      case 'view_once': return '#DA0037';
      case '24_hours': return '#FFA500';
      case 'unlimited': return '#4CAF50';
      default: return '#9CA3AF';
    }
  };

  return (
    <Rodal 
      visible={visible} 
      onClose={onClose}
      animation="slideUp"
      closeOnEsc={true}
      closeMaskOnClick={true}
      showCloseButton={false}
      customStyles={{
        modal: {
          backgroundColor: '#1E1E2E',
          borderRadius: '20px',
          padding: '24px',
          maxWidth: '500px',
          width: '90%'
        }
      }}
    >
      <div className="flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-white text-xl font-semibold">
            Compartir Álbum
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <FiX size={24} />
          </button>
        </div>

        {/* Información del álbum */}
        <div className="bg-neutral-900/30 rounded-lg p-4 mb-4">
          <p className="text-gray-400 text-xs mb-1">Álbum</p>
          <p className="text-white text-base font-semibold">
            {albumName}
          </p>
        </div>

        {/* ID del usuario */}
        <div className="mb-4">
          <label className="block text-gray-400 text-sm mb-2">ID del usuario</label>
          <input
            type="text"
            placeholder="Ingresa el ID del usuario"
            value={selectedFriendId}
            onChange={(e) => setSelectedFriendId(e.target.value)}
            className="w-full bg-neutral-900/50 border border-neutral-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-neutral-500"
          />
          <p className="text-gray-500 text-xs mt-2">
            Nota: Debes conocer el ID del usuario para compartir
          </p>
        </div>

        {/* Tipo de acceso */}
        <div className="mb-4">
          <label className="block text-gray-400 text-sm mb-2">Tipo de acceso</label>
          <div className="flex flex-wrap gap-2">
            {['view_once', '24_hours', 'unlimited'].map(type => (
              <button
                key={type}
                onClick={() => setAccessType(type)}
                className={`px-4 py-3 rounded-lg border-2 text-xs font-semibold transition-all ${
                  accessType === type
                    ? 'border-opacity-100'
                    : 'border-neutral-700 bg-neutral-900/30 text-gray-400'
                }`}
                style={{
                  borderColor: accessType === type ? getAccessTypeColor(type) : '#4A4A6A',
                  backgroundColor: accessType === type ? `${getAccessTypeColor(type)}20` : 'rgba(74, 74, 106, 0.3)',
                  color: accessType === type ? getAccessTypeColor(type) : '#9CA3AF'
                }}
              >
                {getAccessTypeLabel(type)}
              </button>
            ))}
          </div>
        </div>

        {/* Fechas (opcional) */}
        {accessType !== 'view_once' && (
          <>
            <div className="mb-4">
              <label className="block text-gray-400 text-sm mb-2">Desde (opcional)</label>
              <input
                type="text"
                placeholder="YYYY-MM-DD HH:MM"
                value={accessFrom}
                onChange={(e) => setAccessFrom(e.target.value)}
                className="w-full bg-neutral-900/50 border border-neutral-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-neutral-500"
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-400 text-sm mb-2">Hasta (opcional)</label>
              <input
                type="text"
                placeholder="YYYY-MM-DD HH:MM"
                value={accessUntil}
                onChange={(e) => setAccessUntil(e.target.value)}
                className="w-full bg-neutral-900/50 border border-neutral-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-neutral-500"
              />
            </div>
          </>
        )}

        {/* Botón de otorgar acceso */}
        <button
          onClick={handleGrantAccess}
          disabled={!selectedFriendId.trim()}
          className="w-full bg-neutral-600 hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-lg transition-colors mt-4"
        >
          Otorgar Acceso
        </button>
      </div>
    </Rodal>
  );
}
